import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Major, Path } from '../../lib/types';
import PathsContentComponent from './PathsContentComp';
import Loading from '../../components/custom/Loading';
import { getGroup, getMajor } from '../../lib/api';
import { useLocation, useParams } from 'react-router-dom';
import MajorDetailTop from '../components/MajorDetailTop';
import { CardHeader, CardTitle } from '../../components/ui/card';
import { CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import PublicError from '../components/PublicError';

function LandingPage() {
  const location = useLocation();
  const id = location.pathname.split('/')[2];
  const iframeRef = useRef<HTMLIFrameElement>(null);


  const [selectedLevelAcademics, setSelectedLevelAcademics] = useState('early');
  const [selectedLevelPath, setSelectedLevelPath] = useState('early');

  const {
    data: major,
    isLoading: isLoadingMajor,
    isError: isMajorError,
  } = useQuery({
    queryFn: async () => {
      try {
        const result = await getMajor(parseInt(id));
        if (result.status !== 200) {
          toast.error('Uh oh❗ Something went wrong.', {
            description: 'There was a problem loading major.',
            cancel: {
              label: 'Cancel',
            },
          });
        }
        return (result.data as Major) || null;
      } catch (error: any) {
        toast.error('Uh oh❗ There was a problem fetching major.', {
          description: error.response.data,
          cancel: {
            label: 'Cancel',
          },
        });
      }
    },
    queryKey: ['major'],
  });

  const {
    data: trackList,
    isError: isTrackError,
    isLoading: isLoadingTracks,
  } = useQuery({
    queryFn: async () => {
      try {
        const result = await getGroup(parseInt(id));
        if (result.status !== 200) {
          toast.error('Uh oh❗ Something went wrong.', {
            description: 'There was a problem loading tracks.',
            cancel: {
              label: 'Cancel',
            },
          });
        }
        return (result.data as Major[]) || [];
      } catch (error: any) {
        toast.error('Uh oh❗ There was a problem loading tracks.', {
          description: error.response.data,
          cancel: {
            label: 'Cancel',
          },
        });
      }
    },
    queryKey: ['track-list'],
  });

  useEffect(() => {
    const height = document.getElementById('landingPage_IFrame')?.clientHeight

    window.parent.postMessage(height, '*');
  }, [])




  if (
    (isLoadingMajor || isLoadingTracks) &&
    (major === null ||
      major === undefined ||
      trackList === null ||
      trackList === undefined)
  ) {
    return <Loading />;
  } else if (isMajorError || isTrackError) {
    return (
      <CardHeader ref={iframeRef} className="min-h-[50vh] flex items-center justify-center">
        <CardTitle className="text-3xl capitalize">
          Oops❗ Something Went Wrong while fetching the major!
        </CardTitle>
        <CardDescription className="text-xl text-center w-[80vw]">
          Make sure to check the id you are using is not the trackId and is a
          number. Id used: <b>{id}</b>. If the id is correct then please wait
          while we resolve the issue.
        </CardDescription>
      </CardHeader>
    );
  } else if (
    major === null ||
    major === undefined ||
    trackList === null ||
    trackList === undefined
  ) {
    return (
      <>
        <CardHeader ref={iframeRef} className="min-h-[50vh] flex items-center justify-center">
          <CardTitle className="text-3xl capitalize">
            Oops❗ Something Went Wrong while fetching the major!
          </CardTitle>
          <CardDescription className="text-xl text-center w-[80vw]">
            Make sure to check the id you are using is not the trackId and is a
            number. Id used: <b>{id}</b>. If the id is correct then please wait
            while we resolve the issue.
          </CardDescription>
        </CardHeader>
      </>
    );
  }

  const paths: Path[] | [] = major?.paths.filter(
    (p: Path) => p.isDraft === false
  );
  const filteredTrackList: Major[] | undefined = trackList?.filter(
    (t) => t.trackId !== t.majorId
  );

  return (
    <section id='landingPage_IFrame' ref={iframeRef} className="bg-white min-h-screen py-10 mt-5">
      <section className="text-black px-2">
        {paths?.length === 0 && major.academicsPublishSet.length !== 3 ? (
          <>
            <div className="mb-5 text-center">
              <h1 className="text-5xl font-bold mb-2">{major?.majorTxt}</h1>
              <span className="font-normal text-gray-500 text-lg">
                {major?.collegeTxt}
              </span>
            </div>
            <PublicError
              title="There does not seem to be anything here 👀"
              description="No published content or paths found for this major!"
            />
          </>
        ) : paths?.length !== 0 ? (
          <>
            <MajorDetailTop
              group={filteredTrackList}
              major={major}
              selectedLevel={selectedLevelAcademics}
              setSelectedLevel={setSelectedLevelAcademics}
              academicsArr={major?.academicsPublishSet}
            />
            <PathsContentComponent
              paths={paths}
              group={filteredTrackList}
              selectedLevel={selectedLevelPath}
              setSelectedLevel={setSelectedLevelPath}
            />
          </>
        ) : (
          <>
            <MajorDetailTop
              group={filteredTrackList}
              major={major}
              selectedLevel={selectedLevelAcademics}
              setSelectedLevel={setSelectedLevelAcademics}
              academicsArr={major?.academicsPublishSet}
            />
            <PublicError
              title="There does not seem to be anything here 👀"
              description="No published paths found for this major!"
            />
          </>
        )}
      </section>
    </section>
  );
}

export default LandingPage;
